import React from "react";
import ReactDOM from "react-dom/client";
import { registerSW } from "virtual:pwa-register";

// Geist Variable + Geist Mono Variable — the typography backbone
import "@fontsource-variable/geist/index.css";
import "@fontsource-variable/geist-mono/index.css";

// Token system (cream + gradient brand) — must load before any component
import "./tokens/index.css";

// Production error + performance telemetry. No-ops in dev.
import { initSentry } from "./lib/sentry";
initSentry();

import { pb } from "./lib/pb";
import { syncAuthCookie } from "./lib/files";
import { listenForInstallPrompt, reportPwaStatus } from "./lib/pwa";

// Capture the install prompt before any component mounts so a later
// "Install app" button can trigger it on demand.
listenForInstallPrompt();

// Auto-update the SW in the background; next hard reload activates it.
registerSW({ immediate: true });

import App from "./App";

/**
 * Cookie-gated boot.
 *
 * When a user reloads a page with a saved PB session, we MUST have the
 * HttpOnly `pb_auth` cookie in place before React renders — otherwise
 * the first batch of <img> elements hits Caddy's cookie gate and 403s,
 * which looks exactly like "all my images are broken" on mobile.
 *
 * We therefore:
 *   • When authStore is valid → await the cookie handshake before render.
 *     Capped at 1.5s so a backend blip never blocks login indefinitely.
 *   • When authStore is empty → render immediately; the login submit
 *     handler does its own explicit await before navigating away.
 */
async function boot() {
  if (pb.authStore.isValid) {
    try {
      await Promise.race([
        syncAuthCookie(),
        new Promise<void>((resolve) => window.setTimeout(resolve, 1500)),
      ]);
    } catch { /* silent — protected routes will retry via useAuth onChange */ }
  }

  ReactDOM.createRoot(document.getElementById("root")!).render(
    <React.StrictMode>
      <App />
    </React.StrictMode>,
  );

  // Best-effort admin-console beacon once the app is up.
  window.addEventListener("load", () => { reportPwaStatus(); });
}

void boot();
